import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { faLock, faPlus } from '@fortawesome/free-solid-svg-icons';
import { CustomStep, DropdownComponent, Tick } from 'marketpro-design-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/services/auth.service';
import { RangeFilter } from '../../../models/custom-filter-range.model';
import { RangeFilterType } from '../../../models/range-filter-type.enum';
import { SpendingGroup } from '../../../models/spending-group.model';
import { DashboardStatisticsService } from '../../../services/dashboard-statistics.service';
import { DataFiltersService } from '../../../services/data-filters.service';
import { UpsaleModalService } from '../../../services/upsale-modal.service';
import { SystemGroupsService } from '../../../services/system-groups.service';
import { TooltipOptions } from 'marketpro-design-lib';
import { TooltipService } from '../../../services/tooltip.service';

@Component({
  selector: 'mpro-spending-group-selector',
  templateUrl: './spending-group-selector.component.html',
  styleUrls: [
    './../dropdown-common.scss',
    './spending-group-selector.component.scss',
  ],
})
export class SpendingGroupSelectorComponent implements OnInit, OnDestroy {
  @ViewChild(DropdownComponent) public dropdown: DropdownComponent;

  public faPlus = faPlus;
  public faLock = faLock;

  @ViewChild('tooltip')
  public tooltip: ElementRef;

  public tooltipOptions: TooltipOptions = {
    type: 'interactive',
    title: '',
    interactiveText: '',
  };

  public middleClassOpened: boolean;
  public inEditSpendingGroup: RangeFilter;
  public max: number = 131;
  public customSteps: CustomStep[] = [
    {
      from: 0,
      to: 131,
      step: 1,
      labelGap: 1,
      customLabels: {
        0: '$0',
        5: '$5',
        8: '$8',
        12: '$12',
        30: '$30',
        50: '$50',
        70: '$70',
        90: '$90',
        110: '$110',
        131: '>$130',
      },
      considerToValueAsUndefined: true,
    },
  ];
  public isDeleteModalOpen: boolean = false;
  public spendingGroupForDeletion: SpendingGroup | undefined = undefined;
  public openedGroup: SpendingGroup = new SpendingGroup();
  addNew = false;
  userGroupsDropdownOpened = false;
  @Input() public selectedSpendingGroups: SpendingGroup[] = [];

  @Input() public inputValue: string = '';
  @Input() public systemGroups: SpendingGroup[] = [];
  @Input() public userGroups: SpendingGroup[] = [];
  @Input() public multiSelect: boolean = false;
  @Input() public hideNew: boolean = false;

  @Output() public multiItemSelection: EventEmitter<SpendingGroup[]> =
    new EventEmitter();
  @Output() public newGroup: EventEmitter<void> = new EventEmitter();
  // might not need 2 emitters
  @Output() public groupClicked: EventEmitter<SpendingGroup> =
    new EventEmitter();
  @Output() public subgroupClicked: EventEmitter<SpendingGroup> =
    new EventEmitter();
  @Output() public spendingGroupsChanged: EventEmitter<void> =
    new EventEmitter();

  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(
    public systemGroupsService: SystemGroupsService,
    public authService: AuthService,
    private dashboardService: DashboardStatisticsService,
    private dataFiltersService: DataFiltersService,
    private upsaleModalService: UpsaleModalService,
    private tooltipService: TooltipService
  ) {}

  public ngOnInit() {
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(({ spendingGroup }) => {
        if (!spendingGroup.preview) {
          this.setAddNew(false);
        }
      });
  }

  public ngAfterViewInit() {
    if (this.tooltip) {
      this.tooltipService.setTooltipElement(
        'SpendingGroupSelector',
        this.tooltip
      );
    }
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public getGroupColor(spendingGroup: SpendingGroup, shade: number) {
    const color = this.systemGroupsService.getSpendingGroupColorHex(
      spendingGroup.greaterThan,
      spendingGroup.lessThan,
      shade
    );

    return color === '#FFFFFF' ? undefined : color;
  }

  public inputValues() {
    return this.selectedSpendingGroups.map((spendingGroup) =>
      spendingGroup.compactDisplayValue()
    );
  }

  public isSpendingGroupDisabled(): boolean {
    return this.selectedSpendingGroups.length >= 6;
  }

  public onGroupClicked(group: SpendingGroup, subGroup: boolean) {
    this.tooltipService.hideTooltip('SpendingGroupSelector');
    if (group.disabled || group.authDisabled) {
      return;
    }
    if (this.multiSelect) {
      if (!group.greaterThan && !group.lessThan) {
        this.selectedSpendingGroups = [];
        this.multiItemSelection.emit(this.selectedSpendingGroups);
        this.dropdown.dropdownOpen = false;
        return;
      }
      if (
        this.selectedSpendingGroups.some(
          (spendingGroup) =>
            spendingGroup.displayValue() === group.displayValue()
        )
      ) {
        this.selectedSpendingGroups = this.selectedSpendingGroups.filter(
          (ageGroup) => ageGroup.displayValue() !== group.displayValue()
        );
      } else {
        this.selectedSpendingGroups.push(group);
      }
      this.multiItemSelection.emit(this.selectedSpendingGroups);
      this.dropdown.dropdownOpen = false;
    }

    this.inputValue = group.displayValue();
    this.dropdown.dropdownOpen = false;
    if (subGroup) {
      this.subgroupClicked.emit(group);
    } else {
      this.groupClicked.emit(group);
    }
  }

  public isGroupSelected(group: SpendingGroup) {
    if (this.multiSelect) {
      return this.inputValues().some(
        (inputValue) => group.compactDisplayValue() === inputValue
      );
    }
    if (group.id != undefined) {
      return (
        group.id === this.dataFiltersService.getDataFiltersValue().ageGroup.id
      );
    }

    return group.displayValue() === this.inputValue;
  }

  onCreated() {
    this.closeCreateWindowAndReloadGroups();
  }

  public onPreview(rangeFilter: RangeFilter) {
    const spendingGroup = SpendingGroup.from(rangeFilter);
    spendingGroup.preview = true;
    this.groupClicked.emit(spendingGroup);
    this.dropdown.toggleDropdown();
  }

  onSave(rangeFilter: RangeFilter) {
    rangeFilter.type = RangeFilterType.SPENDING;

    if (rangeFilter.id) {
      this.dashboardService
        .updateRangeCustomFilter(rangeFilter)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => this.closeCreateWindowAndReloadGroups());
      return;
    }

    this.dashboardService
      .saveRangeCustomFilter(rangeFilter)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.closeCreateWindowAndReloadGroups());
  }

  private closeCreateWindowAndReloadGroups() {
    this.setAddNew(false);
    this.spendingGroupsChanged.emit();
  }

  setAddNew(state: boolean) {
    if (!this.authService.isLoggedIn() && state) {
      this.upsaleModalService.open();
      return;
    }
    this.addNew = state;
    this.inEditSpendingGroup = new RangeFilter('', 45, 85);
  }

  public onDropdownOpened() {
    this.spendingGroupsChanged.emit();
  }

  public onDeleteClicked(spendingGroup: SpendingGroup) {
    if (spendingGroup.id === undefined) {
      return;
    }

    this.spendingGroupForDeletion = spendingGroup;
    this.isDeleteModalOpen = true;
  }

  public onDelete() {
    if (!this.spendingGroupForDeletion || !this.spendingGroupForDeletion.id) {
      return;
    }

    this.isDeleteModalOpen = false;
    this.dashboardService
      .deleteRangeCustomFilter(this.spendingGroupForDeletion.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.spendingGroupForDeletion = undefined;
        this.spendingGroupsChanged.emit();
      });
  }

  public onEditClicked(spendingGroup: SpendingGroup) {
    this.setAddNew(true);
    this.inEditSpendingGroup =
      RangeFilter.fromSpendingIncomeGroup(spendingGroup);
  }

  public onCancel() {
    this.setAddNew(false);
    this.inEditSpendingGroup = new RangeFilter('', 45, 85);
    if (this.dataFiltersService.getDataFiltersValue().spendingGroup.preview) {
      this.groupClicked.emit(this.systemGroups[0]);
    }
  }

  public userGroupsClicked() {
    this.userGroupsDropdownOpened = !this.userGroupsDropdownOpened;
  }

  public groupArrowClicked(group: SpendingGroup, showSubgroups: boolean) {
    this.openedGroup.opened = false;
    group.opened = showSubgroups;
    this.openedGroup = group;
  }

  public getInputValue() {
    if (!this.multiSelect || this.inputValues().length === 0) {
      return this.inputValue;
    }
    return this.inputValues().join(', ');
  }

  public onMouseEnter(ev: MouseEvent, spendingGroup: SpendingGroup) {
    if (spendingGroup && spendingGroup.name === 'Extremely poor') {
      this.tooltipOptions = {
        type: 'interactive',
        title: `$2 is actually $2.15`,
        interactiveText: `(international definition of extreme poverty)`,
      };
    } else if (this.isSpendingGroupDisabled()) {
      this.tooltipOptions = {
        type: 'data-explorer',
        interactiveText:
          'You can’t select this because you already have 6 spending groups selected',
      };
    } else {
      return;
    }

    this.tooltipService.showTooltip(ev, 'SpendingGroupSelector');
  }

  public onMouseLeave() {
    this.tooltipService.hideTooltip('SpendingGroupSelector');
  }
}
